import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from "@/lib/queryClient";
import { toLocalDateString } from "@/lib/dateUtils";
import { formatOptionalLocaleDate } from "@/lib/optionalDate";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Users, MapPin, AlertTriangle, CheckCircle, XCircle, Music, DollarSign } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { formatDisplayText } from "@/lib/displayText";

const bookingSchema = z.object({
  weddingId: z.number(),
  danceProgramId: z.number(),
  eventDate: z.string().min(1, "Event date is required"),
  eventStartTime: z.string().min(1, "Event time is required"),
  eventEndTime: z.string().min(1, "Event time is required"),
});

type BookingFormData = z.infer<typeof bookingSchema>;

// Loads Razorpay checkout.js once and reports readiness (mirrors van-rental.tsx).
function useRazorpayScript() {
  const [loaded, setLoaded] = useState(
    typeof window !== "undefined" && !!(window as any).Razorpay
  );
  useEffect(() => {
    if (loaded) return;
    if ((window as any).Razorpay) {
      setLoaded(true);
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );
    if (existing) {
      existing.addEventListener("load", () => setLoaded(true));
      if ((window as any).Razorpay) setLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);
  }, [loaded]);
  return loaded;
}

interface DanceProgramRequest {
  id: number;
  weddingId: number;
  eventDate: string | null;
  eventStartTime: string;
  eventEndTime: string;
  status: 'pending' | 'schedule_approved' | 'payment_pending' | 'approved' | 'rejected';
  approvalStage: string;
  isAvailable: boolean;
  scheduleConflicts?: string;
  paymentRequired: number;
  paymentReceived: boolean;
  rejectionReason?: string;
  adminNotes?: string;
  createdAt: string;
  reviewedAt?: string;
  venue?: string;
  guestCount?: number;
  wedding: {
    title: string;
    brideName?: string;
    groomName?: string;
  };
}

interface ScheduleValidation {
  isAvailable: boolean;
  conflicts: Array<{
    type: string;
    conflictDetails: string;
    suggestedAlternatives?: string[];
  }>;
  checkedDate: string;
  checkedTimeSlot: string;
  venue: string;
}

export default function DanceProgramBooking() {
  const [isValidatingSchedule, setIsValidatingSchedule] = useState(false);
  const [scheduleValidation, setScheduleValidation] = useState<ScheduleValidation | null>(null);
  const [selectedWedding, setSelectedWedding] = useState<number | null>(null);
  const razorpayReady = useRazorpayScript();
  const queryClient = useQueryClient();

  // Fetch user's weddings
  const { data: weddings = [] } = useQuery({
    queryKey: ['/api/weddings'],
    queryFn: () => apiRequest('/api/weddings').then(res => res.json())
  });

  // Fetch user's dance program requests
  const { data: requests = [], isLoading: requestsLoading } = useQuery({
    queryKey: ['/api/dance-program/my-requests'],
    queryFn: () => apiRequest('/api/dance-program/my-requests').then(res => res.json())
  });

  // Fetch wedding components (to get dance program options)
  const { data: components = [], isLoading: isComponentsLoading } = useQuery({
    queryKey: ['/api/wedding-components'],
    queryFn: () => apiRequest('/api/wedding-components').then(res => res.json()),
    staleTime: 0
  });

  const dancePrograms = components.filter((c: any) => 
    c.category === 'entertainment' && c.name.toLowerCase().includes('dance')
  );

  console.log('All components:', components.length);
  console.log('Entertainment components:', components.filter((c: any) => c.category === 'entertainment'));
  console.log('Dance programs found:', dancePrograms);

  const form = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      weddingId: 0,
      danceProgramId: 0,
      eventDate: '',
      eventStartTime: '',
      eventEndTime: ''
    }
  });

  // Check if selected wedding already has a pending dance request
  const hasExistingRequest = (weddingId: number | null) => {
    if (!weddingId || !requests.length) return false;
    return requests.some((r: DanceProgramRequest) => 
      r.weddingId === weddingId && 
      ['pending', 'schedule_approved', 'payment_pending', 'approved'].includes(r.status)
    );
  };

  const existingRequestForWedding = selectedWedding ? 
    requests.find((r: DanceProgramRequest) => 
      r.weddingId === selectedWedding && 
      ['pending', 'schedule_approved', 'payment_pending', 'approved'].includes(r.status)
    ) : null;

  // Auto-fill event times when wedding is selected
  useEffect(() => {
    if (selectedWedding && weddings.length > 0) {
      const wedding = weddings.find((w: any) => w.id === selectedWedding);
      if (!wedding) return;

      // Extract time information from wedding details
      const weddingTime = wedding.weddingTime || wedding.timeSlot;
      let startTime = '18:00'; // Default evening time
      let endTime = '22:00';   // Default end time
      
      // Parse time from notes if available
      if (wedding.notes && wedding.notes.includes('Time Slot:')) {
        const timeMatch = wedding.notes.match(/Time Slot:\s*(\d{1,2}:\d{2}\s*[AP]M)\s*to\s*(\d{1,2}:\d{2}\s*[AP]M)/i);
        if (timeMatch) {
          const convertTo24Hour = (time12h: string) => {
            const [time, modifier] = time12h.split(/\s*(AM|PM)/i);
            let [hours, minutes] = time.split(':');
            if (hours === '12') hours = '00';
            if (modifier?.toUpperCase() === 'PM') hours = String(parseInt(hours, 10) + 12);
            return `${hours.padStart(2, '0')}:${minutes}`;
          };
          
          startTime = convertTo24Hour(timeMatch[1]);
          endTime = convertTo24Hour(timeMatch[2]);
        }
      } else if (weddingTime) {
        // Handle other time formats
        if (weddingTime.includes('PM') || weddingTime.includes('Evening')) {
          startTime = '18:00';
          endTime = '22:00';
        } else if (weddingTime.includes('AM') || weddingTime.includes('Morning')) {
          startTime = '10:00';
          endTime = '14:00';
        }
      }
      
      // Convert wedding date to YYYY-MM-DD format (IST-safe)
      const eventDate = toLocalDateString(wedding.weddingDate);
      
      // Set all form values
      form.setValue('weddingId', wedding.id);
      form.setValue('eventDate', eventDate);
      form.setValue('eventStartTime', startTime);
      form.setValue('eventEndTime', endTime);
      
      // Set default dance program if available
      if (dancePrograms.length > 0) {
        const defaultProgramId = dancePrograms[0].id;
        form.setValue('danceProgramId', defaultProgramId);
        console.log('Setting dance program ID:', defaultProgramId, 'from programs:', dancePrograms);
      } else {
        console.log('No dance programs available');
      }
      
      console.log('Auto-populated form values:', {
        weddingId: wedding.id,
        eventDate,
        eventStartTime: startTime,
        eventEndTime: endTime,
        danceProgramId: dancePrograms[0]?.id || 0,
        availablePrograms: dancePrograms.length
      });
    }
  }, [selectedWedding, form, weddings, dancePrograms, requests]);

  // Validate schedule mutation
  const validateScheduleMutation = useMutation({
    mutationFn: async (data: { eventDate: string; eventStartTime: string; eventEndTime: string; weddingId?: number }) => {
      const response = await apiRequest('/api/dance-program/validate-schedule', {
        method: 'POST',
        data
      });
      return response.json();
    },
    onSuccess: (data: ScheduleValidation) => {
      setScheduleValidation(data);
      setIsValidatingSchedule(false);
    },
    onError: (error: any) => {
      toast({
        title: "Validation Error",
        description: error.message || "Failed to validate schedule",
        variant: "destructive",
      });
      setIsValidatingSchedule(false);
    }
  });

  // Submit booking request mutation
  const submitBookingMutation = useMutation({
    mutationFn: async (data: BookingFormData) => {
      const response = await apiRequest('/api/dance-program/request', {
        method: 'POST',
        data
      });
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/dance-program/my-requests'] });
      toast({
        title: "Request Submitted",
        description: data.message || "Dance program booking request submitted successfully.",
      });
      form.reset();
      setScheduleValidation(null);
      setSelectedWedding(null);
    },
    onError: (error: any) => {
      toast({
        title: "Submission Error",
        description: error.message || "Failed to submit booking request",
        variant: "destructive",
      });
    }
  });

  const handleScheduleValidation = () => {
    const eventDate = form.getValues('eventDate');
    const eventStartTime = form.getValues('eventStartTime');
    const eventEndTime = form.getValues('eventEndTime');

    if (!eventDate || !eventStartTime || !eventEndTime) {
      toast({
        title: "Validation Error",
        description: "Please fill in event date and time before validating schedule.",
        variant: "destructive",
      });
      return;
    }

    setIsValidatingSchedule(true);
    validateScheduleMutation.mutate({
      eventDate,
      eventStartTime,
      eventEndTime,
      // The server excludes this wedding's own crew bookings from the overlap check.
      weddingId: selectedWedding ?? undefined
    });
  };

  const onSubmit = (data: BookingFormData) => {
    console.log("Form submission triggered with data:", data);
    
    if (!scheduleValidation) {
      toast({
        title: "Validation Required",
        description: "Please validate your schedule before submitting the request.",
        variant: "destructive",
      });
      return;
    }

    console.log("Schedule validation passed, submitting booking request");
    submitBookingMutation.mutate(data);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending Review</Badge>;
      case 'schedule_approved':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">Schedule Approved</Badge>;
      case 'payment_pending':
        return <Badge variant="secondary" className="bg-orange-100 text-orange-800">Payment Pending</Badge>;
      case 'approved':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  }

  const handlePayment = async (request: DanceProgramRequest) => {
    try {
      if (!(window as any).Razorpay) {
        toast({
          title: "Please wait",
          description: "Payment system is still loading. Try again in a moment.",
        });
        return;
      }

      // Create Razorpay order — amount is computed SERVER-SIDE (not sent by client)
      const orderResponse = await apiRequest(`/api/dance-program/create-order/${request.id}`, {
        method: 'POST',
      });

      const orderData = await orderResponse.json();
      if (!orderResponse.ok || !orderData.orderId) {
        throw new Error(orderData.error || 'Failed to create payment order');
      }

      // Initialize Razorpay payment (key + amount come from the server response)
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'LA Mirage Koratty',
        description: 'Dance Program by Dsouls Dance Company',
        order_id: orderData.orderId,
        handler: async function (response: any) {
          try {
            // Verify payment and update dance program status
            const verifyResponse = await apiRequest(`/api/dance-program/verify-payment/${request.id}`, {
              method: 'POST',
              data: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              }
            });

            if (verifyResponse.ok) {
              toast({
                title: "Payment Successful",
                description: "Your dance program booking has been confirmed!",
              });
              queryClient.invalidateQueries({ queryKey: ['/api/dance-program/my-requests'] });
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error) {
            toast({
              title: "Payment Verification Failed",
              description: "Please contact support if amount was deducted.",
              variant: "destructive",
            });
          }
        },
        prefill: {
          name: `${request.wedding.brideName || ''} ${request.wedding.groomName || ''}`.trim(),
          email: '', // Will be filled from user profile
        },
        theme: {
          color: '#B8860B'
        }
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (error: any) {
      toast({
        title: "Payment Error",
        description: error.message || "Failed to initiate payment",
        variant: "destructive",
      });
    }
  };

  const getConflictDetails = (scheduleConflicts?: string) => {
    if (!scheduleConflicts) return null;
    
    try {
      const conflicts = JSON.parse(scheduleConflicts);
      return conflicts.map((conflict: any, index: number) => (
        <div key={index} className="p-3 bg-red-50 border border-red-200 rounded-lg mb-2">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <span className="font-medium text-red-700 capitalize">{conflict.type.replace('_', ' ')}</span>
          </div>
          <p className="text-sm text-red-600 mb-2">{conflict.conflictDetails}</p>
          {conflict.suggestedAlternatives && (
            <div>
              <p className="text-xs font-medium text-red-700 mb-1">Suggested Alternatives:</p>
              <ul className="text-xs text-red-600 list-disc list-inside">
                {conflict.suggestedAlternatives.map((alt: string, altIndex: number) => (
                  <li key={altIndex}>{alt}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ));
    } catch {
      return <p className="text-sm text-red-600">{scheduleConflicts}</p>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {formatDisplayText("Dance Program Booking")}
          </h1>
          <p className="text-gray-300">
            Request professional dance programs for your event
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Booking Form */}
          <Card className="bg-gray-800/60 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Music className="h-5 w-5 text-amber-500" />
                New Booking Request
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* Wedding Selection */}
                  <FormField
                    control={form.control}
                    name="weddingId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300">Select Event</FormLabel>
                        <Select 
                          onValueChange={(value) => {
                            field.onChange(parseInt(value));
                            setSelectedWedding(parseInt(value));
                          }}
                        >
                          <FormControl>
                            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                              <SelectValue placeholder="Choose your event" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {weddings.map((wedding: any) => (
                              <SelectItem key={wedding.id} value={wedding.id.toString()}>
                                {formatDisplayText(wedding.title)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Dance Program Selection */}
                  <FormField
                    control={form.control}
                    name="danceProgramId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300">Dance Program Type</FormLabel>
                        <Select 
                          onValueChange={(value) => field.onChange(parseInt(value))}
                          value={field.value ? field.value.toString() : ""}
                        >
                          <FormControl>
                            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                              <SelectValue placeholder="Select dance program" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {dancePrograms.map((program: any) => (
                              <SelectItem key={program.id} value={program.id.toString()}>
                                {program.name} {program.price && `(₹${program.price})`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Event Date - Calendar Picker */}
                  <FormField
                    control={form.control}
                    name="eventDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300">Event Date</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            {...field}
                            className="bg-gray-700 border-gray-600 text-white"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Event Time - Auto-filled from client details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="eventStartTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">Event Start Time</FormLabel>
                          <FormControl>
                            <Input
                              type="time"
                              {...field}
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="eventEndTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">Event End Time</FormLabel>
                          <FormControl>
                            <Input
                              type="time"
                              {...field}
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Schedule Validation */}
                  <div className="space-y-4">
                    <Button
                      type="button"
                      onClick={handleScheduleValidation}
                      disabled={isValidatingSchedule}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {isValidatingSchedule ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                          Validating Schedule...
                        </>
                      ) : (
                        <>
                          <Clock className="h-4 w-4 mr-2" />
                          Validate Schedule
                        </>
                      )}
                    </Button>

                    {/* Schedule Validation Results */}
                    {scheduleValidation && (
                      <div className={`p-4 rounded-lg border ${
                        scheduleValidation.isAvailable 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          {scheduleValidation.isAvailable ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500" />
                          )}
                          <span className={`font-medium ${
                            scheduleValidation.isAvailable ? 'text-green-800' : 'text-red-800'
                          }`}>
                            {scheduleValidation.isAvailable 
                              ? 'Schedule Available' 
                              : 'Schedule Conflicts Detected'
                            }
                          </span>
                        </div>
                        
                        <p className={`text-sm ${
                          scheduleValidation.isAvailable ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {scheduleValidation.checkedDate} • {scheduleValidation.checkedTimeSlot} • {scheduleValidation.venue}
                        </p>

                        {!scheduleValidation.isAvailable && scheduleValidation.conflicts.length > 0 && (
                          <div className="mt-3">
                            {scheduleValidation.conflicts.map((conflict, index) => (
                              <div key={index} className="p-3 bg-red-100 border border-red-200 rounded-lg mb-2">
                                <div className="flex items-center gap-2 mb-1">
                                  <AlertTriangle className="h-4 w-4 text-red-500" />
                                  <span className="font-medium text-red-700 capitalize">
                                    {conflict.type.replace('_', ' ')}
                                  </span>
                                </div>
                                <p className="text-sm text-red-600 mb-2">{conflict.conflictDetails}</p>
                                {conflict.suggestedAlternatives && (
                                  <div>
                                    <p className="text-xs font-medium text-red-700 mb-1">Suggested Alternatives:</p>
                                    <ul className="text-xs text-red-600 list-disc list-inside">
                                      {conflict.suggestedAlternatives.map((alt, altIndex) => (
                                        <li key={altIndex}>{alt}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Duplicate Request Warning */}
                  {existingRequestForWedding && (
                    <div className="p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                        <span className="font-bold text-yellow-800">
                          Existing Request Found
                        </span>
                      </div>
                      <p className="text-sm text-yellow-700 mb-3">
                        You already have a dance program request for <strong>{formatDisplayText(existingRequestForWedding.wedding.title)}</strong>.
                        Only one dance program can be booked per wedding.
                      </p>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-yellow-700">Status:</span>
                        {getStatusBadge(existingRequestForWedding.status)}
                      </div>
                      <p className="text-xs text-yellow-600 mt-2">
                        Please select a different event or wait for your current request to be processed.
                      </p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={submitBookingMutation.isPending || !scheduleValidation || !!existingRequestForWedding}
                    className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                    data-testid="button-submit-dance-request"
                  >
                    {submitBookingMutation.isPending ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Submitting Request...
                      </>
                    ) : existingRequestForWedding ? (
                      <>
                        <XCircle className="h-4 w-4 mr-2" />
                        Cannot Submit - Duplicate Request
                      </>
                    ) : (
                      <>
                        <Music className="h-4 w-4 mr-2" />
                        Submit Booking Request
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Previous Requests */}
          <Card className="bg-gray-800/60 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Your Booking Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {requestsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-gray-300">Loading requests...</p>
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-8">
                  <Music className="h-12 w-12 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-400">No dance program requests yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {requests.map((request: DanceProgramRequest) => (
                    <div key={request.id} className="p-4 bg-gray-700/50 rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-white">
                            {formatDisplayText(request.wedding.title)}
                          </h4>
                          <p className="text-sm text-gray-400">
                            {formatOptionalLocaleDate(request.eventDate)} • 
                            {request.eventStartTime} - {request.eventEndTime}
                          </p>
                        </div>
                        {getStatusBadge(request.status)}
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-300 mb-3">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-amber-500" />
                          {request.venue}
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-amber-500" />
                          {request.guestCount || 'TBD'} guests
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-amber-500" />
                          ₹{request.paymentRequired.toLocaleString()}
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-amber-500" />
                          {request.paymentReceived ? 'Paid' : 'Payment Pending'}
                        </div>
                      </div>

                      {!request.isAvailable && request.scheduleConflicts && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-red-400 mb-2">Schedule Conflicts:</p>
                          {getConflictDetails(request.scheduleConflicts)}
                        </div>
                      )}

                      {request.rejectionReason && (
                        <div className="p-3 bg-red-900/30 border border-red-700/50 rounded-lg mb-3">
                          <p className="text-sm font-medium text-red-400 mb-1">Rejection Reason:</p>
                          <p className="text-sm text-red-300">{request.rejectionReason}</p>
                        </div>
                      )}

                      {request.adminNotes && (
                        <div className="p-3 bg-blue-900/30 border border-blue-700/50 rounded-lg mb-3">
                          <p className="text-sm font-medium text-blue-400 mb-1">Admin Notes:</p>
                          <p className="text-sm text-blue-300">{request.adminNotes}</p>
                        </div>
                      )}

                      {/* Payment Button for Payment Pending Status */}
                      {request.status === 'payment_pending' && !request.paymentReceived && (
                        <div className="p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg mb-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-amber-400 mb-1">Payment Required</p>
                              <p className="text-sm text-amber-300">Your request has been approved. Complete payment to confirm booking.</p>
                            </div>
                            <Button
                              onClick={() => handlePayment(request)}
                              disabled={!razorpayReady}
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                            >
                              <DollarSign className="h-4 w-4 mr-2" />
                              {razorpayReady ? `Pay ₹${request.paymentRequired.toLocaleString()}` : 'Loading…'}
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="text-xs text-gray-500 border-t border-gray-600 pt-2">
                        Submitted: {new Date(request.createdAt).toLocaleString()}
                        {request.reviewedAt && (
                          <span> • Reviewed: {new Date(request.reviewedAt).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}